import { posthog } from 'posthog-js'

import type { IUser } from '~/types/user-info-types'

import appConfig from '~/config/app'

import { pick } from '..'

/**
 * `posthog.reset()` for a logout or account switch. The reset also clears the stored consent, and the
 * school build starts opted out (`opt_out_capturing_by_default`) and opts in only on a full page load
 * (`instrumentation-client.ts`). Without opting back in here, everything after an in-app logout or the
 * Okta callback (identify, survey events, session replay) is dropped until the next reload.
 */
export const resetPosthogIdentity = (): void => {
  posthog.reset()
  if (appConfig.isSchoolPlatform) posthog.opt_in_capturing({ captureEventName: null })
}

export const identifyUserWithPosthog = (data: Pick<IUser, 'id'> & Partial<IUser>, locale?: string) => {
  // The school platform identifies by user id only — the properties below (authEmail, birthYear,
  // country, …) are not sent.
  if (appConfig.isSchoolPlatform) {
    posthog.identify(`${data.id}`)
    return
  }

  const userId = `${data.id}`

  const personProperties = pick(data, [
    'authEmail',
    'birthYear',
    'country',
    'countryCode',
    'createdAt',
    'curriculumCountryCode',
    'email',
    'emailStatus',
    'emailVerified',
    'facebookEmail',
    'fullName',
    'gradeLevelId',
    'nickname',
    'phone',
    'pseudoId',
    'role',
    'skill',
    'timezone',
    'userCountry',
    'userType',
    'wantsNewsletter',
    'yearsInSchool',
  ] as const)

  const snakeCaseProperties = Object.entries(personProperties)
    .filter(([, value]) => value !== undefined && value !== null)
    .reduce((acc, [key, value]) => {
      const snakeKey = key.replace(/[A-Z]/g, (match) => `_${match.toLowerCase()}`)
      if (value) acc[snakeKey] = `${value}`
      return acc
    }, {} as Record<string, string>)

  const properties: Record<string, string> = { ...snakeCaseProperties }
  if (locale) properties.locale = locale

  posthog.identify(userId, properties)
}
